import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, AlertCircle, Loader, RefreshCw, ArrowRight, Clock, WifiOff, ShieldCheck, Shield } from 'lucide-react';
import Logo from '../components/ui/Logo';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';

const REDIRECT_DELAY = 5;
const RESEND_COOLDOWN = 60;
const PRODUCTION_URL = 'https://leadgenpr6367.builtwithrocket.new';
// How long to wait for detectSessionInUrl to auto-process the token before falling back to manual verifyOtp
const AUTO_DETECT_TIMEOUT_MS = 3000;

const getErrorDetails = (errorMsg = '') => {
  const msg = (errorMsg || '')?.toLowerCase();
  if (msg?.includes('expired') || msg?.includes('otp_expired') || msg?.includes('token has expired') || msg?.includes('invalid otp')) {
    return {
      title: 'Link Expired',
      message: 'Your confirmation link has expired. Links are valid for 24 hours.',
      hint: 'Please request a new confirmation email below.',
      type: 'expired',
      icon: Clock,
      iconColor: 'text-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
    };
  }
  if (msg?.includes('invalid') || msg?.includes('not found') || msg?.includes('bad_jwt') || msg?.includes('already confirmed') || msg?.includes('already been used') || msg?.includes('email link is invalid')) {
    return {
      title: 'Invalid or Used Link',
      message: 'This confirmation link is invalid or has already been used.',
      hint: 'Please request a new confirmation email or register again.',
      type: 'invalid',
      icon: AlertCircle,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
    };
  }
  if (msg?.includes('network') || msg?.includes('fetch') || msg?.includes('failed to fetch') || msg?.includes('networkerror')) {
    return {
      title: 'Network Error',
      message: 'Unable to connect to the server. Please check your internet connection.',
      hint: 'Try refreshing the page or check your connection.',
      type: 'network',
      icon: WifiOff,
      iconColor: 'text-gray-500',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
    };
  }
  if (msg?.includes('rate limit') || msg?.includes('too many')) {
    return {
      title: 'Too Many Attempts',
      message: 'Too many requests. Please wait a few minutes before trying again.',
      hint: 'Wait a few minutes, then try again.',
      type: 'ratelimit',
      icon: Clock,
      iconColor: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
    };
  }
  return {
    title: 'Confirmation Failed',
    message: errorMsg || 'The confirmation link may have expired or is invalid.',
    hint: 'Please try registering again or contact support.',
    type: 'generic',
    icon: AlertCircle,
    iconColor: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  };
};

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState('loading');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);
  const [resendCooldown, setResendCooldown] = useState(0);
  const countdownRef = useRef(null);
  const resendCooldownRef = useRef(null);
  const toastShownRef = useRef(false);
  const resolvedRef = useRef(false); // prevents double-resolution

  // Parse hash params from URL (Supabase uses hash for errors and implicit-flow tokens)
  const hashParams = React.useMemo(() => {
    try {
      const hash = window?.location?.hash?.replace('#', '');
      if (!hash) return {};
      return Object.fromEntries(new URLSearchParams(hash));
    } catch { return {}; }
  }, []);

  const pendingEmail = searchParams?.get('email') ||
    (() => { try { return localStorage?.getItem('pendingConfirmationEmail') || ''; } catch { return ''; } })();
  const isPending = searchParams?.get('pending') === 'true';
  const type = searchParams?.get('type');

  // Supabase PKCE flow sends token_hash + type in URL params
  const tokenHash = searchParams?.get('token_hash');
  const confirmationType = searchParams?.get('type') || 'email';

  // Detect hash-based errors from Supabase (e.g. otp_expired, access_denied)
  const hashError = hashParams?.error;
  const hashErrorCode = hashParams?.error_code;
  const hashErrorDescription = hashParams?.error_description;

  // Also check for access_token in hash (implicit flow fallback)
  const hashAccessToken = hashParams?.access_token;
  const hashRefreshToken = hashParams?.refresh_token;

  const startCountdown = useCallback(() => {
    setCountdown(REDIRECT_DELAY);
    if (countdownRef?.current) clearInterval(countdownRef?.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef?.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startResendCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN);
    if (resendCooldownRef?.current) clearInterval(resendCooldownRef?.current);
    resendCooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(resendCooldownRef?.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (countdownRef?.current) clearInterval(countdownRef?.current);
      if (resendCooldownRef?.current) clearInterval(resendCooldownRef?.current);
    };
  }, []);

  const markConfirmed = useCallback(() => {
    if (resolvedRef?.current) return;
    resolvedRef.current = true;
    setStatus('confirmed');
    if (!toastShownRef?.current) {
      toastShownRef.current = true;
      toast?.success('Your email has been verified successfully! Redirecting to dashboard...', 'Email Confirmed ✓', 6000);
    }
    try { localStorage?.removeItem('pendingConfirmationEmail'); } catch {}
    startCountdown();
    setTimeout(() => navigate('/dashboard', { replace: true }), REDIRECT_DELAY * 1000);
  }, [toast, startCountdown, navigate]);

  const markError = useCallback((errorMsg) => {
    if (resolvedRef?.current) return;
    resolvedRef.current = true;
    const details = getErrorDetails(errorMsg);
    setStatus('error');
    setErrorDetails(details);
    if (!toastShownRef?.current) {
      toastShownRef.current = true;
      toast?.error(details?.message, details?.title, 8000);
    }
  }, [toast]);

  // ─── Primary: Listen for Supabase auth events (detectSessionInUrl auto-processes token_hash) ───
  useEffect(() => {
    if (!supabase) return;

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        if (session?.user) {
          // Clean up the URL
          try { window.history?.replaceState(null, '', window.location?.pathname); } catch {}
          if (confirmationType === 'recovery') {
            if (!resolvedRef?.current) {
              resolvedRef.current = true;
              navigate('/reset-password', { replace: true });
            }
          } else {
            markConfirmed();
          }
        }
      }
    });

    return () => subscription?.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationType, markConfirmed, navigate]);

  // ─── Secondary: Handle all other cases + fallback manual verifyOtp ───────────
  useEffect(() => {
    const handleConfirmation = async () => {
      // 1. Handle hash-based errors from Supabase
      if (hashError) {
        const errorMsg = hashErrorDescription?.replace(/\+/g, ' ') || hashErrorCode || hashError;
        markError(errorMsg);
        try { window.history?.replaceState(null, '', window.location?.pathname + window.location?.search); } catch {}
        return;
      }

      // 2. Handle implicit flow: access_token in hash
      if (hashAccessToken && hashRefreshToken) {
        try {
          const { data, error } = await supabase?.auth?.setSession({
            access_token: hashAccessToken,
            refresh_token: hashRefreshToken,
          });
          if (error) { markError(error?.message); return; }
          if (data?.session || data?.user) {
            try { window.history?.replaceState(null, '', window.location?.pathname); } catch {}
            markConfirmed();
            return;
          }
        } catch (err) {
          markError(err?.message || '');
          return;
        }
      }

      // 3. token_hash present — let detectSessionInUrl handle it first.
      //    Wait AUTO_DETECT_TIMEOUT_MS for onAuthStateChange to fire.
      //    If it fires, resolvedRef.current will be true and we skip manual verifyOtp.
      if (tokenHash) {
        // First check: maybe session already exists (page reload scenario)
        try {
          const { data: existingSession } = await supabase?.auth?.getSession();
          if (existingSession?.session?.user) {
            try { window.history?.replaceState(null, '', window.location?.pathname); } catch {}
            if (confirmationType === 'recovery') {
              if (!resolvedRef?.current) {
                resolvedRef.current = true;
                navigate('/reset-password', { replace: true });
              }
              return;
            }
            markConfirmed();
            return;
          }
        } catch {}

        // Wait for detectSessionInUrl to auto-process the token via onAuthStateChange
        await new Promise(resolve => setTimeout(resolve, AUTO_DETECT_TIMEOUT_MS));

        // If already resolved by onAuthStateChange, we're done
        if (resolvedRef?.current) return;

        // Fallback: manually call verifyOtp
        try {
          const { data, error } = await supabase?.auth?.verifyOtp({
            token_hash: tokenHash,
            type: confirmationType === 'recovery' ? 'recovery' : 'email',
          });
          if (error) {
            // If "already confirmed" but we have a session, treat as success
            if (
              error?.message?.toLowerCase()?.includes('already confirmed') ||
              error?.message?.toLowerCase()?.includes('already been used')
            ) {
              try {
                const { data: sessionCheck } = await supabase?.auth?.getSession();
                if (sessionCheck?.session?.user) {
                  markConfirmed();
                  return;
                }
              } catch {}
            }
            markError(error?.message);
            return;
          }
          if (data?.session || data?.user) {
            try { window.history?.replaceState(null, '', window.location?.pathname); } catch {}
            if (confirmationType === 'recovery') {
              if (!resolvedRef?.current) {
                resolvedRef.current = true;
                navigate('/reset-password', { replace: true });
              }
              return;
            }
            markConfirmed();
            return;
          }
          // verifyOtp returned no error and no session — unexpected
          markError('Verification completed but no session was created. Please try signing in.');
        } catch (err) {
          markError(err?.message || '');
        }
        return;
      }

      // 4. Recovery type without token (just showing pending state)
      if (type === 'recovery') {
        setStatus('recovery');
        if (!toastShownRef?.current) {
          toastShownRef.current = true;
          toast?.info('Check your inbox for the password reset link.', 'Reset Email Sent', 5000);
        }
        return;
      }

      // 5. Pending state (just registered, waiting for email click)
      if (isPending) {
        setStatus('pending');
        if (!toastShownRef?.current) {
          toastShownRef.current = true;
          toast?.info('Check your inbox for the confirmation link.', 'Confirmation Email Sent', 5000);
        }
        return;
      }

      // 6. Already authenticated user
      if (user) {
        markConfirmed();
        return;
      }

      // 7. Check for existing session as final fallback
      if (!supabase) {
        markError('network error');
        return;
      }

      try {
        const { data: { session }, error } = await supabase?.auth?.getSession();
        if (error) { markError(error?.message); return; }
        if (session?.user) {
          markConfirmed();
        } else {
          setStatus('pending');
          if (!toastShownRef?.current) {
            toastShownRef.current = true;
            toast?.info('Check your inbox for the confirmation link.', 'Confirmation Pending', 5000);
          }
        }
      } catch (err) {
        markError(err?.message || 'network error');
      }
    };

    handleConfirmation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendEmail = async () => {
    if (!pendingEmail || resendCooldown > 0 || resending) return;
    setResending(true);
    setResendSuccess(false);
    try {
      if (!supabase) throw new Error('Authentication service unavailable');
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: pendingEmail,
        options: {
          emailRedirectTo: `${PRODUCTION_URL}/email-confirmation`
        }
      });
      if (error) {
        if (error?.message?.toLowerCase()?.includes('rate limit') || error?.message?.toLowerCase()?.includes('too many')) {
          toast?.warning('Too many requests. Please wait a few minutes before trying again.', 'Rate Limited');
        } else {
          throw error;
        }
      } else {
        setResendSuccess(true);
        toast?.success(`Confirmation email resent to ${pendingEmail}`, 'Email Sent ✓');
        startResendCooldown();
        setTimeout(() => setResendSuccess(false), 5000);
      }
    } catch (err) {
      console.error('Resend error:', err);
      toast?.error(err?.message || 'Failed to resend confirmation email. Please try again.', 'Resend Failed');
    } finally {
      setResending(false);
    }
  };

  // ─── Loading State ───────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Verifying your email...</h2>
          <p className="text-gray-500 text-sm">Please wait while we confirm your account.</p>
        </div>
      </div>
    );
  }

  // ─── Confirmed State ─────────────────────────────────────────────────────────
  if (status === 'confirmed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo size="large" variant="default" showText={true} linkTo="/" />
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">Email Confirmed! 🎉</h1>
            <p className="text-gray-600 text-center mb-2">
              Your email has been successfully verified. Welcome to LeadGen Pro!
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Redirecting to dashboard in <span className="font-bold text-green-800">{countdown}</span> second{countdown !== 1 ? 's' : ''}...
                </span>
              </div>
              <div className="mt-2 bg-green-200 rounded-full h-1.5">
                <div
                  className="bg-green-600 h-1.5 rounded-full transition-all duration-1000"
                  style={{ width: `${((REDIRECT_DELAY - countdown) / REDIRECT_DELAY) * 100}%` }}
                />
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
            >
              <span>Go to Dashboard Now</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Error State ─────────────────────────────────────────────────────────────
  if (status === 'error' && errorDetails) {
    const ErrorIcon = errorDetails?.icon || AlertCircle;
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo size="large" variant="default" showText={true} linkTo="/" />
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className={`w-16 h-16 ${errorDetails?.bgColor || 'bg-red-50'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <ErrorIcon className={`w-8 h-8 ${errorDetails?.iconColor || 'text-red-500'}`} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">{errorDetails?.title}</h1>
            <p className="text-gray-600 text-center mb-2">{errorDetails?.message}</p>
            <p className="text-sm text-gray-500 text-center mb-6">{errorDetails?.hint}</p>

            {pendingEmail && errorDetails?.type !== 'network' && errorDetails?.type !== 'ratelimit' && (
              <div className={`${errorDetails?.bgColor || 'bg-red-50'} border ${errorDetails?.borderColor || 'border-red-200'} rounded-xl p-4 mb-6`}>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Resend confirmation to: <span className="font-bold">{pendingEmail}</span>
                </p>
                {resendSuccess ? (
                  <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Email resent! Check your inbox.</span>
                  </div>
                ) : (
                  <button
                    onClick={handleResendEmail}
                    disabled={resending || resendCooldown > 0}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed text-blue-700 rounded-xl font-medium text-sm transition-all"
                  >
                    {resending ? (
                      <><Loader className="w-4 h-4 animate-spin" /><span>Sending...</span></>
                    ) : resendCooldown > 0 ? (
                      <><Clock className="w-4 h-4" /><span>Resend available in {resendCooldown}s</span></>
                    ) : (
                      <><RefreshCw className="w-4 h-4" /><span>Resend Confirmation Email</span></>
                    )}
                  </button>
                )}
                {resendCooldown > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Cooldown</span>
                      <span>{resendCooldown}s remaining</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-1000"
                        style={{ width: `${((RESEND_COOLDOWN - resendCooldown) / RESEND_COOLDOWN) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-colors"
              >
                Register Again
              </Link>
              <Link
                to="/login"
                className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Sign In
              </Link>
            </div>

            <p className="text-center text-xs text-gray-400 mt-4">
              Need help?{' '}
              <a href="mailto:support@leadgenproai.us" className="text-blue-600 hover:underline">
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Recovery State ───────────────────────────────────────────────────────────
  if (status === 'recovery') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo size="large" variant="default" showText={true} linkTo="/" />
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Password Reset Email Sent</h1>
            <p className="text-gray-600 mb-4">Check your inbox for the password reset link.</p>
            <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6">
              <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-700">Reset links expire after <span className="font-semibold">1 hour</span>.</p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <span>Back to Sign In</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Pending State (default) ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size="large" variant="default" showText={true} linkTo="/" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-blue-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
            Check Your Email
          </h1>
          <p className="text-gray-600 text-center mb-2">
            We've sent a confirmation link to:
          </p>
          {pendingEmail && (
            <p className="text-blue-600 font-semibold text-center mb-6 break-all">
              {pendingEmail}
            </p>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
            <p className="text-sm font-semibold text-blue-800 mb-3">What to do next:</p>
            <ol className="space-y-2">
              {[
                'Check your inbox (and spam folder)',
                'Click the confirmation link in the email',
                'You\'ll be automatically signed in',
              ]?.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-blue-700">
                  <span className="w-5 h-5 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {pendingEmail && (
            <div className="mb-6">
              <p className="text-sm text-gray-500 text-center mb-3">Didn't receive the email?</p>
              {resendSuccess ? (
                <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Email resent! Check your inbox.</span>
                </div>
              ) : (
                <button
                  onClick={handleResendEmail}
                  disabled={resending || resendCooldown > 0}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed text-blue-700 rounded-xl font-medium text-sm transition-all"
                >
                  {resending ? (
                    <><Loader className="w-4 h-4 animate-spin" /><span>Sending...</span></>
                  ) : resendCooldown > 0 ? (
                    <><Clock className="w-4 h-4" /><span>Resend available in {resendCooldown}s</span></>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /><span>Resend Confirmation Email</span></>
                  )}
                </button>
              )}
              {resendCooldown > 0 && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Cooldown</span>
                    <span>{resendCooldown}s remaining</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-1000"
                      style={{ width: `${((RESEND_COOLDOWN - resendCooldown) / RESEND_COOLDOWN) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2 mb-6">
            <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span>Confirmation links expire after 24 hours for your security.</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/login"
              className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Back to Sign In
            </Link>
            <Link
              to="/register"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors"
            >
              Register Again
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Need help?{' '}
          <a href="mailto:support@leadgenproai.us" className="text-blue-600 hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default EmailConfirmation;